// The next draft, ready before the reviewer gets there (#315, stage 2 / C).
//
// Every אשר והבא / דלג / מחק והבא used to wait, in sequence, for the next
// document, then its catalog match, behind a full-page spinner. This module
// holds ONE draft ahead — the one the queue says comes next — keyed by
// restaurant + document id, so DocumentViewPage can show it straight away.
//
// All of it is fetched EARLY, while the reviewer is still reading the
// current draft (owner, 25/10/2026 — replacing "document and match only after
// the decision's PUT"): the document, its catalog match and the scan's first
// page. One blob at a time: a newer next draft revokes the older blob, and
// so does dropDraftPrefetch.
//
// Early means before the decision, and a decision can change the next
// draft: approving moves the supplier's catalog prices, and every save
// back-fills its delivery notes (server/lib/backfillPrices.js) — with the
// supplier-sorted queue (#313) the next draft is usually that supplier's. So
// the page shows the held copy at once and RE-CHECKS it right after the
// decision (DocumentViewPage revalidateWarmDraft): the server's copy, and a
// fresh catalog match — always, since any approval of the supplier, by any
// reviewer, can move catalog prices (a held match is never trusted). The decisions
// stay locked until that re-check is back; a change it finds is applied in
// place, or — when the reviewer has already typed — noted in amber.
//
// The page moves on once the document and its match are held (at once, after
// the early load), or after a short bound (whenDraftPrefetchSettles) when the
// early load had nothing yet. The scan is never waited for: the pane takes
// over a download still in flight (Codex review).
//
// Taking an entry hands it over: the caller owns the blob from then on and
// revokes it itself (DocumentViewPage, when the next document supersedes it
// or on unmount) — the module never revokes a blob it has handed out, so the
// viewer can't lose its page mid-review.
//
// The page revalidates what it takes (draftFingerprint): another admin may
// have decided or edited the draft meanwhile. Nothing here decides that.
//
// Module state, like the skipped set in draftQueue.js: it lives as long as
// the page does. A restaurant change drops it (the key carries the
// restaurant, and the api tenant fence would refuse a crossed request
// anyway).

// A document held longer than this is fetched again at the decision. The
// re-check makes any copy safe; this only keeps a long read from showing a
// very old one first. The scan has no limit — a stored file never changes.
export const DRAFT_PREFETCH_TTL_MS = 10 * 60 * 1000;

let entry = null; // { rid, docId, scan, scanPending, scanPromise, scanHanded, draft }

const sameKey = (e, rid, docId) => Boolean(e) && e.rid === (rid ?? null) && String(e.docId) === String(docId);

const revoke = (url, revokeUrl) => {
  if (url && url.startsWith('blob:')) revokeUrl(url);
};

const defaultRevoke = (url) => URL.revokeObjectURL(url);

// The entry for (rid, docId), replacing — and cleaning up — any other.
function entryFor(rid, docId, revokeUrl) {
  if (sameKey(entry, rid, docId)) return entry;
  dropDraftPrefetch({ revokeUrl });
  entry = { rid: rid ?? null, docId: String(docId), scan: null, scanPending: null, draft: null };
  return entry;
}

/**
 * Fetch the next draft's first scan page as an object URL. `fetchUrl(filePath)`
 * resolves to a blob: URL (or an external one, kept as is). Idempotent for
 * the same draft and page; a different draft supersedes the held one.
 */
export function prefetchDraftScan({ rid, docId, filePath, fetchUrl, revokeUrl = defaultRevoke }) {
  if (docId == null || !filePath || typeof fetchUrl !== 'function') return;
  const e = entryFor(rid, docId, revokeUrl);
  if (e.scan?.filePath === filePath || e.scanPending === filePath) return;
  if (e.scan) { revoke(e.scan.url, revokeUrl); e.scan = null; }
  e.scanPending = filePath;
  e.scanHanded = false;
  // Resolves to { filePath, url } for whoever took the entry while the page
  // was still downloading (scanHanded) — that caller owns it — else null.
  e.scanPromise = Promise.resolve()
    .then(() => fetchUrl(filePath))
    .then((url) => {
      if (e.scanHanded) return url ? { filePath, url } : null;
      // Superseded while in flight (another draft, or dropped): not ours to keep.
      if (entry !== e || e.scanPending !== filePath) { revoke(url, revokeUrl); return null; }
      e.scanPending = null;
      if (url) e.scan = { filePath, url };
      return null;
    })
    .catch((err) => {
      if (entry === e && e.scanPending === filePath) e.scanPending = null;
      // The viewer fetches the page itself; a failed prefetch only costs time.
      console.warn('[draft-prefetch] scan prefetch failed', err?.message || err);
      return null;
    });
}

/**
 * Resolves once the document held for (rid, docId) — and whatever `load`
 * bundled with it — has arrived or failed, or after `timeoutMs`, whichever
 * comes first. Never rejects. The page waits on this before moving to the
 * next draft, so it lands warm instead of on a spinner. Not the scan: see
 * the header. No document loading for that draft → resolves at once.
 */
export function whenDraftPrefetchSettles({ rid, docId, timeoutMs = 1500 }) {
  if (!sameKey(entry, rid, docId) || !entry.draft) return Promise.resolve();
  let timer;
  const timeout = new Promise(resolve => { timer = setTimeout(resolve, timeoutMs); });
  return Promise.race([Promise.allSettled([entry.draft.promise]), timeout]).then(() => { clearTimeout(timer); });
}

/**
 * Start loading the next draft's document (and whatever else `load` bundles
 * with it — the page adds its catalog match). Idempotent while the held one
 * is fresh; an expired one is loaded again. `load()` resolves to `{ doc, ... }`.
 */
export function prefetchDraftDoc({ rid, docId, load, revokeUrl = defaultRevoke, now = Date.now() }) {
  if (docId == null || typeof load !== 'function') return;
  const e = entryFor(rid, docId, revokeUrl);
  if (e.draft && now - e.draft.at <= DRAFT_PREFETCH_TTL_MS) return;
  const draft = { value: null, promise: null, at: now };
  draft.promise = Promise.resolve()
    .then(load)
    .then((value) => {
      // A response for another document is not this draft (never in practice;
      // cheap to refuse).
      if (!value?.doc || String(value.doc.id) !== String(docId)) throw new Error('prefetched document does not match');
      draft.value = value;
      return value;
    });
  // Rejections surface to whoever takes the promise; none unhandled meanwhile.
  draft.promise.catch(() => {});
  e.draft = draft;
}

/**
 * Hand over what is held for (rid, docId) and forget it. Returns
 * `{ value, promise, startedAt, scan, scanPending }` — `value` when the document has
 * arrived, `promise` while it is still loading, `scan` ({ filePath, url })
 * when the first page is ready (the caller now owns that URL), `scanPending`
 * ({ filePath, promise }) while it is still downloading: the promise
 * resolves to the scan, owned by the caller, or null — so the viewer waits
 * for it instead of downloading the same page a second time — or null when
 * nothing useful is held. Anything held for another draft stays put.
 */
export function takeDraftPrefetch({ rid, docId, now = Date.now() }) {
  if (!sameKey(entry, rid, docId)) return null;
  const e = entry;
  entry = null;
  const draft = e.draft && now - e.draft.at <= DRAFT_PREFETCH_TTL_MS ? e.draft : null;
  let scanPending = null;
  if (!e.scan && e.scanPending) {
    e.scanHanded = true;
    scanPending = { filePath: e.scanPending, promise: e.scanPromise };
  }
  const out = {
    value: draft?.value || null,
    promise: draft && !draft.value ? draft.promise : null,
    // When the document's load started (epoch ms): one started after the
    // decision was written needs no re-check of what that decision changed.
    startedAt: draft ? draft.at : null,
    scan: e.scan,
    scanPending,
  };
  return out.value || out.promise || out.scan || out.scanPending ? out : null;
}

/** Forget everything held, revoking a blob not yet handed out. */
export function dropDraftPrefetch({ revokeUrl = defaultRevoke } = {}) {
  if (entry) revoke(entry.scan?.url, revokeUrl);
  entry = null;
}

/** What the queue holds right now — for tests. */
export function peekDraftPrefetchForTests() {
  return entry ? { rid: entry.rid, docId: entry.docId, scan: entry.scan, hasDraft: Boolean(entry.draft) } : null;
}

// What revalidation compares: the fields the review form opens with, and the
// lines. Equal fingerprints mean the cached copy shows what the server has.
// The supplier flags shape the form's lines (deposit / purchase tax); the
// counts and the duplicate link are on screen beside it.
const FINGERPRINT_FIELDS = [
  'status', 'supplier_id', 'doc_type', 'doc_num', 'date', 'subtotal', 'vat', 'total',
  'invoice_discount', 'invoice_discount_type', 'is_price_diff_credit', 'duplicate_warning',
  'supplier_name', 'supplier_has_deposit', 'supplier_has_purchase_tax',
  'comment_count', 'payment_note_count',
];
export function draftFingerprint(doc) {
  if (!doc) return '';
  const head = [...FINGERPRINT_FIELDS.map(k => doc[k] ?? null), doc.duplicate_of?.id ?? null];
  return JSON.stringify([head, doc.items || [], (doc.files || []).map(f => f.file_path)]);
}
